/* eslint-disable react/jsx-props-no-spreading */

import {
  IconClockHour3,
  IconInfoCircle,
  IconMessageCircle, IconPencil, IconPhone, IconVideo,
} from "@tabler/icons-react";
import { ReactNode } from "react";

import Switch, { SwitchProps } from "../Switch";

export enum Status {
  offline = "offline",
  working = "working",
  online = "online",
  break = "break",
}

export enum Channel {
  phone = "phone",
  livechat = "livechat",
  video = "video",
  written = "written",
}

export interface SwitchStatusProps {
  switchProps: Omit<SwitchProps, "customStyle | value | icons | subLabel | labelPosition">;
  status: Status;
  availableChannels?: Channel[];
}

const statusStyle: Record<Status, string> = {
  offline: "before:bg-switch-status-bg-offline",
  working: "before:bg-switch-status-bg-working",
  online: "before:bg-switch-status-bg-online",
  break: "before:bg-switch-status-bg-break",
};

const statusLabels: Record<Status, string> = {
  offline: "Vous n'êtes pas disponible",
  working: "En consultation depuis",
  online: "Vous êtes disponible",
  break: "Retour prévu à",
};

const statusIcons: Record<Status, ReactNode> = {
  offline: <IconInfoCircle />,
  working: <IconClockHour3 />,
  online: <IconInfoCircle />,
  break: <IconClockHour3 />,
};

const subLabelStyle = `
  hidden lg:flex h-5 w-fit items-center rounded-sm border border-overlay typography-small-base pr-2 py-0.5 gap-0.5
`;

const subLabelColors: Record<Status, string> = {
  offline: "text-badge-red-icon bg-badge-red-bg",
  working: "text-badge-amber-icon bg-badge-amber-bg",
  online: "text-badge-green-icon bg-badge-green-bg",
  break: "text-badge-blue-icon bg-badge-blue-bg",
};

const switchStyle = `
  w-10 before:w-10 h-0 before:h-5 checked:after:translate-x-5 after:w-4 after:h-4 after:mt-0.5 after:ml-0.5
  before:border before:switch-status-border after:bg-bg-switch-handle-default
`;

const getIcon = (channel: Channel, status: Status) => {
  if ([Status.working, Status.break].includes(status)) {
    return <img className="hidden md:inline" height="20px" width="20px" src={`../../assets/${channel}-waiting.svg`} alt="statusIcon" />;
  }
  switch (channel) {
    case Channel.phone:
      return <IconPhone className="hidden md:inline" />;
    case Channel.livechat:
      return <IconMessageCircle className="hidden md:inline" />;
    case Channel.video:
      return <IconVideo className="hidden md:inline" />;
    case Channel.written:
      return <IconPencil className="hidden md:inline" />;
    default:
      return <></>;
  }
};

const getSubLabel = (status: Status) => (
  <div className={`${subLabelStyle} ${subLabelColors[status]}`}>
    {statusIcons[status]}
    <span>{statusLabels[status]}</span>
  </div>
);

const SwitchStatus = ({
  switchProps, status, availableChannels = [],
}: SwitchStatusProps) => (
  <Switch
    {...switchProps}
    label={<span className="hidden md:inline">{switchProps.label}</span>}
    labelPosition="left"
    customStyle={`${switchStyle} ${statusStyle[status]}`}
    value={!![Status.online, Status.working].includes(status)}
    icons={[
      availableChannels.includes(Channel.phone) && getIcon(Channel.phone, status),
      availableChannels.includes(Channel.livechat) && getIcon(Channel.livechat, status),
      availableChannels.includes(Channel.video) && getIcon(Channel.video, status),
      availableChannels.includes(Channel.written) && getIcon(Channel.written, status),
    ].filter(Boolean)}
    subLabel={getSubLabel(status)}
  />
);

export default SwitchStatus;
